import { create } from "zustand";

import type { SessionResponse, User } from "@/types/api";

interface AuthState {
  user: User | null;
  /**
   * Held in memory only. It is deliberately never written to localStorage or
   * sessionStorage: a token a script can read is a token an XSS can steal.
   * Reloading the page recovers the session through the HttpOnly refresh
   * cookie instead -- see BootGate.
   */
  accessToken: string | null;
  /** False until the boot-time refresh has resolved, either way. */
  initialized: boolean;

  setSession: (session: SessionResponse) => void;
  setUser: (user: User) => void;
  markInitialized: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  initialized: false,

  setSession: (session) =>
    set({ user: session.user, accessToken: session.accessToken, initialized: true }),

  setUser: (user) => set({ user }),

  markInitialized: () => set({ initialized: true }),

  // Keeps `initialized` true: the app has already booted, there is simply
  // nobody signed in.
  clear: () => set({ user: null, accessToken: null, initialized: true }),
}));

/* Narrow selectors, so a component re-renders only when the slice it reads
   actually changes. */
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.accessToken !== null);
export const useAuthInitialized = () => useAuthStore((state) => state.initialized);
export const useIsAdmin = () => useAuthStore((state) => state.user?.role === "admin");
